import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import RevendeurStats from "./RevendeurStats";
import RevendeurEvents from "./RevendeurEvents";
import RevendeurFiles from "./RevendeurFiles";
import RevendeurGallery from "./RevendeurGallery";
import API from "../../Api/Api";
import { API_END_POINT, IMAGE_END_POINT } from "../../Api/EndPoints";
import { toast } from "react-toastify";
import Button from "../Common/FormElements/Button/Button";
import RevendeurInstallations from "./RevendeurInstallations";
import RevendeurList from "./RevendeurList";
import RevendeurFilters from "./RevendeurFilters";
import AnnualRevendeurList from "./AnnualRevendeurList";
import AnnualTurnover from "./AnnualTurnover";
import { useTranslation } from "react-i18next";
import { isDefined } from "../../Utils";

const RevendeurViewContent = () => {
  const { t } = useTranslation("common");
  const [reseller, setReseller] = useState({});
  const [filters, setFilters] = useState({});
  const { user } = useSelector((state) => state.userReducer);
  const [userRole, setUserRole] = useState(user?.attributes?.userRole);
  const [id, setId] = useState(
    user?.attributes?.profil?.data?.attributes?.reseller?.data?.id,
  );

  const isManager = userRole === 2;
  const isAdmin = userRole === 1;

  const addMediaToGallery = (list) => {
    setReseller((prev) => ({
      ...prev,
      attributes: {
        ...prev?.attributes,
        gallery: { data: [...list, ...prev?.attributes?.gallery?.data] },
      },
    }));
  };

  const removeMediaFromGallery = async (id) => {
    try {
      await API.delete(`/api/upload/files/${id}`);
      setReseller((prev) => ({
        ...prev,
        attributes: {
          ...prev?.attributes,
          gallery: {
            data: prev?.attributes?.gallery?.data?.filter(
              (media) => +media?.id !== +id,
            ),
          },
        },
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const addInstallation = (installation) => {
    setReseller((prev) => ({
      ...prev,
      attributes: {
        ...prev?.attributes,
        installations: {
          data: [installation, ...prev?.attributes?.installations?.data],
        },
      },
    }));
  };

  // The store's logo, printed next to Poseidon's on its customers' quotes.
  const logoInputRef = useRef(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const logoUrl = reseller?.attributes?.logo?.data?.attributes?.url;

  const uploadLogo = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !reseller?.id) return;
    setLogoLoading(true);
    try {
      const form = new FormData();
      form.append("files", file);
      const { data } = await API.post("/api/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const logo = data?.[0];
      await API.put(`/api/organizations/${reseller.id}`, {
        data: { logo: logo.id },
      });
      setReseller((prev) => ({
        ...prev,
        attributes: {
          ...prev?.attributes,
          logo: { data: { id: logo.id, attributes: { url: logo.url } } },
        },
      }));
      toast.success(t("Logo updated"));
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    } finally {
      setLogoLoading(false);
      event.target.value = "";
    }
  };

  const abortControllerRef = useRef(null);

  useEffect(() => {
    // TEMP
    if (userRole !== 0) return;
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    (async function () {
      try {
        const {
          data: { data },
        } = await API.get(`${API_END_POINT}api/organizations/${id}`, {
          signal,
          params: {
            populate: {
              logo: { fields: ["id", "url"] },
              gallery: {
                fields: ["id", "name", "mime", "url"],
                sort: ["createdAt:desc"],
              },
              installations: {
                fields: ["id", "refClient"],
                populate: {
                  media: { fields: ["id", "name", "mime", "url"] },
                },
                sort: ["createdAt:desc"],
              },
              contract: {
                fields: ["id"],
                populate: {
                  file: { fields: ["name", "url"] },
                  warranty: { fields: ["name", "url"] },
                  area: { fields: ["name", "url"] },
                  products: { fields: ["id", "name", "name_en"] },
                  pvls: { fields: ["id", "name", "name_en"] },
                },
              },
            },
          },
        });
        setReseller(data);
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
          return;
        console.log(error);
      }
    })();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [id, userRole]);

  useEffect(() => {
    if (isDefined(filters?.reseller)) {
      setId(filters.reseller.code);
      setUserRole(0);
    } else if (isDefined(filters?.business_affiliate)) {
      setId(filters?.business_affiliate?.code);
      setUserRole(2);
    } else {
      setUserRole(user?.attributes?.userRole);
      setId(user?.attributes?.profil?.data?.attributes?.reseller?.data?.id);
    }
  }, [
    filters,
    user?.attributes?.profil?.data?.attributes?.reseller?.data?.id,
    user?.attributes?.userRole,
  ]);

  /* ******************************** Rendering ******************************* */
  return (
    <div className="flex-1">
      <div className="">
        <h2 className="text-blue bold f30 mb-1">
          {isAdmin || isManager || user?.attributes?.userRole !== 0
            ? t("Stores")
            : t("My Store")}
          {filters.reseller && ` - ${filters?.reseller?.name}`}
        </h2>
        {[1, 2]?.includes(user?.attributes?.userRole) && (
          <RevendeurFilters filters={filters} setFilters={setFilters} />
        )}
        <RevendeurStats filters={filters} userRole={userRole} />
        {[1, 2]?.includes(userRole) && (
          <>
            {userRole === 1 && <AnnualTurnover filters={filters} />}
            <AnnualRevendeurList filters={filters} userRole={userRole} />
          </>
        )}
        <RevendeurEvents filters={filters} />
      </div>
      {userRole === 0 && (
        <>
          <div className="py-4">
            <div className="d-flex alignH justify-content-between mb-3">
              <div>
                <span className="text-blue bold f22 quickSand">
                  {t("Store logo")}
                </span>
                <p className="f13 text-brightGrey mb-0">
                  {t("Printed on your customers' quotes, next to the Poseidon logo.")}
                </p>
              </div>
              <Button
                loading={logoLoading}
                onClick={() => logoInputRef.current?.click()}
              >
                {t(logoUrl ? "Change the logo" : "Add a logo")}
              </Button>
            </div>
            {logoUrl && (
              <img
                src={`${IMAGE_END_POINT}${logoUrl}`}
                alt={reseller?.attributes?.name ?? ""}
                style={{ maxHeight: 120, maxWidth: 300, objectFit: "contain" }}
              />
            )}
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg"
              hidden
              onChange={uploadLogo}
            />
          </div>
          <RevendeurFiles contract={reseller?.attributes?.contract} />
          <RevendeurGallery
            gallery={reseller?.attributes?.gallery}
            resellerId={reseller?.id}
            addMediaToGallery={addMediaToGallery}
            removeMediaFromGallery={removeMediaFromGallery}
          />
          <RevendeurInstallations
            resellerName={reseller?.attributes?.name}
            installations={reseller?.attributes?.installations}
            resellerId={reseller?.id}
            addInstallation={addInstallation}
          />
        </>
      )}
      {[1, 2]?.includes(userRole) && <RevendeurList filters={filters} />}
    </div>
  );
};

export default RevendeurViewContent;
